import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            // Skip auth check on login page
            if (location.pathname === "/login") {
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get("/api/auth/check");
                if (response.data.user) {
                    setUser(response.data.user);
                }
            } catch (error) {
                console.error("Auth check error:", error);
                if (location.pathname !== "/login") {
                    navigate("/login");
                }
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, [navigate, location.pathname]);

    const login = async (email, password) => {
        try {
            const response = await axios.post("/api/auth/login", {
                email,
                password,
            });

            if (response.data.success && response.data.user) {
                setUser(response.data.user);
                return { success: true, user: response.data.user };
            } else {
                console.error("Invalid login response:", response.data);
                return {
                    success: false,
                    error: "Invalid login response",
                };
            }
        } catch (error) {
            console.error("Login error:", error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || "Napaka pri prijavi",
            };
        }
    };

    const logout = async () => {
        try {
            await axios.post("/api/auth/logout");
            setUser(null);
            navigate("/login");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Only show loading state when not on login page
    if (loading && location.pathname !== "/login") {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{ user, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export default AuthContext;
